import React, { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const tooltipStyle = { background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 };
const METRICS = [
  { key: "attendance", label: "Attendance %", color: "#059669" },
  { key: "sterility", label: "Sterility %", color: "#38bdf8" },
  { key: "consent", label: "Consent %", color: "#C9A24B" },
  { key: "sop", label: "SOP %", color: "#a78bfa" },
  { key: "result_quality", label: "Result Quality %", color: "#059669" },
  { key: "prep", label: "OT Prep %", color: "#38bdf8" },
  { key: "education", label: "Education %", color: "#C9A24B" },
];

export default function BranchComparison() {
  const { filters } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/branch-comparison", { params: { start: filters.start, end: filters.end } }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [filters.start, filters.end]);

  if (loading) return <Empty text="Loading…" />;

  const radarData = METRICS.map((m) => {
    const o = { metric: m.label.replace(" %", "") };
    rows.forEach((r) => { o[r.branch] = r[m.key]; });
    return o;
  });
  const colors = ["#C9A24B", "#059669", "#38bdf8"];

  return (
    <div className="animate-fade-up" data-testid="branch-comparison-page">
      <PageHeader title="Branch-Wise Comparison" subtitle="Sarita Vihar vs Mathura vs Jaipur — side by side across all 7 pillars." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {rows.map((r, i) => (
          <Card key={r.branch} className="p-5" data-testid={`branch-card-${r.branch}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg" style={{ color: colors[i] }}>{r.branch}</h3>
              <span className="text-xs text-slate-400">{r.headcount} technicians</span>
            </div>
            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <div><div className="text-slate-500 text-xs">Attendance</div><div className="font-semibold">{r.attendance}%</div></div>
              <div><div className="text-slate-500 text-xs">Procedures</div><div className="font-semibold">{r.procedures}</div></div>
              <div><div className="text-slate-500 text-xs">Dosing Errors</div><div className={`font-semibold ${r.dosing_errors > 0 ? "text-[#DC2626]" : "text-emerald-400"}`}>{r.dosing_errors}</div></div>
              <div><div className="text-slate-500 text-xs">Complications</div><div className={`font-semibold ${r.complications > 0 ? "text-[#DC2626]" : "text-emerald-400"}`}>{r.complications}</div></div>
              <div><div className="text-slate-500 text-xs">SOP Adherence</div><div className="font-semibold">{r.sop}%</div></div>
              <div><div className="text-slate-500 text-xs">Cross-sell</div><div className="font-semibold text-[#C9A24B]">{fmt(r.upsell_value)}</div></div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">7-Pillar Compliance Radar</h3>
          <ResponsiveContainer width="100%" height={340}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#1e293b" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              {rows.map((r, i) => <Radar key={r.branch} name={r.branch} dataKey={r.branch} stroke={colors[i]} fill={colors[i]} fillOpacity={0.15} />)}
            </RadarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4">Cross-sell Revenue by Branch</h3>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="branch" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `₹${v/1000}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Bar dataKey="upsell_value" name="Cross-sell" fill="#C9A24B" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
